/**
 * Opening the Pictures view (spec 026 D-6): every link to it (the chat card,
 * the handoff, the studio strip, the publish confirm) navigates to
 * `?view=pictures` with its optional `by`, `filter` and `slot` keys, and the
 * story editor switches views from the query. Outside a router (a component
 * rendered on its own in a test) it does nothing.
 */
import { useCallback } from 'react';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';

export type PicturesBy = 'cast' | 'scene';
export type PicturesFilter = 'all' | 'needed' | 'filled';

export interface PicturesQuery {
  by?: PicturesBy;
  filter?: PicturesFilter;
  slot?: string;
}

/** `?view=pictures` plus the given keys, in a fixed order. */
export function picturesSearch(q: PicturesQuery = {}): string {
  const params = new URLSearchParams();
  params.set('view', 'pictures');
  if (q.by) params.set('by', q.by);
  if (q.filter) params.set('filter', q.filter);
  if (q.slot) params.set('slot', q.slot);
  return `?${params.toString()}`;
}

function useRouterOpen(): (q?: PicturesQuery) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback((q?: PicturesQuery) => {
    navigate({ pathname: location.pathname, search: picturesSearch(q) }, { state: location.state });
  }, [navigate, location.pathname, location.state]);
}

const noop = () => {};
const useNoRouterOpen = (): ((q?: PicturesQuery) => void) => noop;

/** A function that opens the Pictures view with the given query. */
export function useOpenPictures(): (q?: PicturesQuery) => void {
  // Whether a router is present never changes for a mounted component.
  const inRouter = useInRouterContext();
  return (inRouter ? useRouterOpen : useNoRouterOpen)();
}
